import store from "../store";
import {
  getStoredCurrentSite,
  getConvergedCookie,
  getStoredTabFabricMode,
  getLocalStorageFlag,
  isAdminRole
} from "./common";
import { getCookie } from "../serviceWorker";
import {
  sidebarMenuItems,
  mainTabs,
  vManageTabsConfig
} from "../config/routeConfig";
import { features } from "./enums";

// get sidebar navigation menu items in vanalytics
export const getMenuItems = features => {
  const vManageURI = getCookie("cl-vManageURI");
  // check license
  const menuItems = sidebarMenuItems.map(item => {
    const menuItem = { ...item };
    if (menuItem.isVManageMenuItem === true)
      // check vmanage menu item in cross launch
      menuItem.available = vManageURI ? true : false;
    else {
      let subMenuItems =
        Array.isArray(menuItem.submenu) ? menuItem.submenu : [];
      // check submenu access
      subMenuItems = subMenuItems.map(value => {
        const subMenuItem = { ...value, text: value.label };
        const hasCheck = Array.isArray(subMenuItem.features)
          && subMenuItem.features.length > 0;
        subMenuItem.available = !hasCheck;
        subMenuItem.enabled = !hasCheck;
        if (hasCheck === true) {
          // check feature access
          for (const feature of subMenuItem.features) {
            if (features.available.includes(feature)) {
              subMenuItem.available = true;
              if (subMenuItem.enabled !== true)
                subMenuItem.enabled = features.enabled.includes(feature);
            }
          }
        }
        // check admin role access
        if (
          subMenuItem.available === true && subMenuItem.access.admin === true
        ) {
          let roles = localStorage.getItem("roles");
          if (roles) roles = JSON.parse(roles);
          subMenuItem.available = isAdminRole(roles);
        }
        return subMenuItem;
      });
      menuItem.submenu = subMenuItems.filter(item => item.available === true);
      menuItem.available = menuItem.submenu.length > 0;
    }
    return menuItem;
  });

  return menuItems.filter(item => item.available === true);
};

export const getMainTabs = features => {
  let tabs = [];
  const isUIconvergence = getCookie("cl-analytics");

  // ui convergence
  if (isUIconvergence) {
    let monitorTabs = getLocalStorageFlag("cl-monitor-tabs");

    // Check if monitorTabs data exists
    if (monitorTabs) {
      // Parse the JSON string to get the tabs array
      monitorTabs = JSON.parse(monitorTabs);

      // Ensure monitorTabs is a valid non-empty array
      if (Array.isArray(monitorTabs) && monitorTabs.length > 0) {
        // Transform each monitor tab to match the expected format
        tabs = monitorTabs.map(tab => {
          // Filter out the first (overview) and last (logs) tabs from mainTabs
          // as these tabs are not available in vAnalytics

          // Check if the tab URL contains 'analytics'
          //if it does, it is a vAnalytics tab
          const vATab = tab.url.includes("analytics");

          // If a matching vAnalytics tab is found, use vA path
          if (vATab) {
            return { ...tab, path: tab.url };
          }

          // If no match found, create a tab with a URL based on the last part
          // of the original URL (after the last dot)
          // this use case is for vM tabs
          return {
            ...tab,
            label: tab.label,
            url: "/" + tab.url?.split(".").pop(),
          }
        });
        return tabs;
      }
    }
    let vManageTabs = getLocalStorageFlag("cl-navigation");
    let validTabs = [];
    const mode = getConvergedCookie("mode");
    const currentSite = getStoredCurrentSite();
    const isNonFabricMode = getStoredTabFabricMode();

    // get valid tabs to display from local storage
    if (vManageTabs) vManageTabs = JSON.parse(vManageTabs);
    if (
      Array.isArray(vManageTabs) && vManageTabs.length > 0
      && Array.isArray(vManageTabs[0].submenu)
    ) {
      validTabs = vManageTabs[0].submenu;
    }

    //Check if tabs are dynamic or static, by looking at existence of tab property
    const isDynamicMenu = validTabs.length > 0 && validTabs[0].tab === true;

    if (isDynamicMenu === true) {
      validTabs = validTabs.filter(item => item.tab === true);
      const vManageTabsLabelConfig = Object.fromEntries(
        Object.values(vManageTabsConfig).map(item => [item.label, item])
      );
      //this code snippet dynamically constructs a list of tabs to be displayed in the user interface 
      //based on various conditions, ensuring that only the relevant tabs are shown to the user.
      validTabs.forEach(item => {
        const tabLabel = item.text;
        if (tabLabel === vManageTabsConfig.security.label) {
          if (mode !== "provider" && currentSite === null)
            tabs.push(vManageTabsConfig.security);
        }
        else if (tabLabel === vManageTabsConfig.multicloud.label) {
          if (mode !== "provider" && currentSite === null)
            tabs.push(vManageTabsConfig.multicloud);
        }
        else if (tabLabel === vManageTabsConfig.tunnels.label) {
          if (mode !== "provider" && isNonFabricMode === false)
            tabs.push(vManageTabsConfig.tunnels);
        }
        else {
          // if item has url use it otherwise construct url from sref
          //i.e. sref is app.monitor2.advisories => /advisories
          const url = item.url || `/${item.sref.split(".").pop()}`;
          if (vManageTabsLabelConfig[tabLabel] instanceof Object)
            tabs.push(vManageTabsLabelConfig[tabLabel]);
          else if (item.url)
            tabs.push({ ...item, label: item.text, path: url });
          else tabs.push({ ...item, label: item.text, url });
        }
      });
    }
    else {
      tabs.push(vManageTabsConfig.overview);
      const tabLabels = validTabs.map(item => item.text);
      if (tabLabels.includes(vManageTabsConfig.devices.label))
        tabs.push(vManageTabsConfig.devices);
      if (mode !== "provider") {
        // these tabs are visible only when all sites is selected
        if (currentSite === null) {
          if (tabLabels.includes(vManageTabsConfig.security.label))
            tabs.push(vManageTabsConfig.security);
          if (tabLabels.includes(vManageTabsConfig.multicloud.label))
            tabs.push(vManageTabsConfig.multicloud);
        }
        // visible based on cookie value
        if (isNonFabricMode === false)
          if (tabLabels.includes(vManageTabsConfig.tunnels.label))
            tabs.push(vManageTabsConfig.tunnels);
        //show energy management tab only for mode !== provider
        if (tabLabels.includes(vManageTabsConfig.energyMangement.label))
          tabs.push(vManageTabsConfig.energyMangement);
      }
      if (tabLabels.includes(vManageTabsConfig.logs.label))
        tabs.push(vManageTabsConfig.logs);

      // vanalytics tabs are added at last
      tabs.push(vManageTabsConfig.applications);
      tabs.push(vManageTabsConfig.sites);
      tabs.push(vManageTabsConfig.circuits);
    }
    // vanalytics
  } else tabs.push(...mainTabs);

  // check license
  tabs = tabs.map(item => {
    const tab = { ...item };
    const hasCheck = Array.isArray(tab.features) && tab.features.length > 0;
    tab.available = !hasCheck;
    tab.enabled = !hasCheck;
    if (hasCheck === true) {
      for (const feature of tab.features) {
        if (features.available.includes(feature)) {
          tab.available = true;
          if (tab.enabled !== true)
            tab.enabled = features.enabled.includes(feature);
        }
      }
    }
    return tab;
  });

  return tabs.filter(item => item.available === true);
};

export const getFeatureAccess = feature => {
  const featuresData =
    store.getState().vanalytics.app.globalFilter.features;
  return {
    available: featuresData.available.includes(feature),
    enabled: featuresData.enabled.includes(feature)
  };
};

export const getBWFAccess = () => getFeatureAccess(features.bwf);
export const getADAccess = () => getFeatureAccess(features.ad);
export const getADUsageAccess = () => getFeatureAccess(features.adUsage);
export const getUMTSAccess = () => getFeatureAccess(features.umts);
export const getPAAccess = () => getFeatureAccess(features.pa);
export const getPPRAccess = () => getFeatureAccess(features.ppr);
export const getInsightsAccess = () => getFeatureAccess(features.insights);
export const getAppQoEOptimizationAccess = () => getFeatureAccess(features.appqoe);
export const getWidgetSummaryAccess = () => getFeatureAccess(features.genaiExplain);
export const getAppFlowAarCorrelationAccess = () => getFeatureAccess(features.appFlowAarCorrelation);
export const getNwpiReadoutAccess = () => getFeatureAccess(features.nwpiReadout);
